import { CampaignAudienceTypeEnum, CampaignBlastStatusesEnum, CampaignBlastTypesEnum } from '../../enums';
import { UserDto } from './user.dto';
import { IsUUID, IsNotEmpty, IsArray, IsString, IsOptional, MaxLength, MinLength, ArrayMinSize } from 'class-validator';

export class CampaignBlastAudienceGroupDto {
	public type: CampaignAudienceTypeEnum;
	public count: number;
	public city_name?: string;
	public state_name?: string;
	public event_baseId?: string;
	public event_instance?: Date | string;
	public event_name?: string;
	public event_avatarId?: string;
}
export class CampaignBlastAudienceUserDto {
	public type: CampaignAudienceTypeEnum;
	public userId: string;
	public city_name?: string;
	public state_name?: string;
	public event_baseId?: string;
	public event_instance?: Date | string;
	public event_name?: string;
	public event_avatarId?: string;
}

export type EventInstanceOption = {
	id: string;
	name: string;
	baseId: string;
	instance: string | Date;
	alias: string;
	shortLocalInstance: string;
	avatarId?: string;
	startDate?: string | Date;
	endDate?: string | Date;
	addressName?: string;
	tzid?: string;
};

export class CampaignBlastAudienceToApplyDto
	implements
		Pick<CampaignBlastAudienceGroupDto, 'type' | 'city_name' | 'state_name' | 'event_baseId' | 'event_instance'>
{
	public type: CampaignAudienceTypeEnum;
	public city_name?: string;
	public state_name?: string;
	public event_baseId?: string;
	public event_instance?: Date | string;
}

export class CreateCampaignBlastBaseDto {
	@IsUUID()
	@IsNotEmpty({ message: 'Community is required' })
	public communityId: string;

	@ArrayMinSize(1, { message: 'At least 1 audience type is required' })
	@IsArray({ message: 'At least 1 audience type is required' })
	@IsNotEmpty({ message: 'Audience is required' })
	public audience: CampaignBlastAudienceToApplyDto[];

	@IsString()
	@IsNotEmpty()
	@MinLength(3, { message: 'Headline must be at least 3 characters' })
	public name: string;

	@IsOptional()
	public startDate?: Date | null;

	@IsOptional()
	@IsString()
	public tzid?: string | null;

	public isDraft?: boolean;
}

export class CreateEmailBlastCampaignDto extends CreateCampaignBlastBaseDto {
	@IsString()
	@IsNotEmpty()
	@MinLength(3, { message: 'Email text must be at least 3 characters' })
	@MaxLength(10_000, { message: 'Email text must contain a maximum of 10 000 characters' })
	public emailMarkdownBody?: string;

	@IsArray()
	@IsOptional()
	public emailLinkedEventInstances?: EventInstanceOption[];
}

export class UpdateCampaignBlastBaseDto {
	@ArrayMinSize(1, { message: 'At least 1 audience type is required' })
	@IsArray({ message: 'At least 1 audience type is required' })
	@IsOptional()
	public audience: CampaignBlastAudienceToApplyDto[];

	@IsOptional()
	@IsString()
	@IsNotEmpty({ message: 'Headline is required' })
	@MinLength(3, { message: 'Headline must be at least 3 characters' })
	public name: string;

	@IsOptional()
	public startDate?: Date | null;

	@IsOptional()
	@IsString()
	public tzid?: string | null;

	public isDraft?: boolean;
}

export class UpdateEmailBlastCampaignDto extends UpdateCampaignBlastBaseDto {
	@IsOptional()
	@IsString()
	@IsNotEmpty({ message: 'Email text is required' })
	@MinLength(3, { message: 'Email text must be at least 3 characters' })
	@MaxLength(10_000, { message: 'Email text must be at least 10 000 characters' })
	public emailMarkdownBody?: string;

	@IsArray()
	@IsOptional()
	public emailLinkedEventInstances?: EventInstanceOption[];
}

export class UpdateSmsBlastCampaignDto extends UpdateCampaignBlastBaseDto {
	@IsOptional()
	@IsString()
	@MinLength(3, { message: 'SMS text must be at least 3 characters' })
	@MaxLength(260, { message: 'SMS text must not exceed 260 characters' })
	public smsText?: string;

	@IsArray()
	@IsOptional()
	public smsLinkedEventInstances?: EventInstanceOption[];
}

export class CreateSmsBlastCampaignDto extends CreateCampaignBlastBaseDto {
	@IsString()
	@IsNotEmpty({ message: 'SMS text is required' })
	@MinLength(3, { message: 'SMS text must be at least 3 characters' })
	@MaxLength(260, { message: 'SMS text must not exceed 260 characters' })
	public smsText: string;

	@IsArray()
	@IsOptional()
	public smsLinkedEventInstances?: EventInstanceOption[];
}

export class CampaignBlastMessageDetailsDto {
	public emailMarkdownBody?: string;
	public emailLinkedEventInstances?: EventInstanceOption[];

	public smsText?: string;
	public smsLinkedEventInstances?: EventInstanceOption[];
}

export class CampaignBlastDto {
	public id: string;
	public name: string;
	public communityId: string;
	public createdByUserId: string;
	public type: CampaignBlastTypesEnum;
	public status: CampaignBlastStatusesEnum;
	public audience: CampaignBlastAudienceToApplyDto[];
	public messageDetails: CampaignBlastMessageDetailsDto;
	public totalUsers?: number;
	public tzid?: string | null;
	public startDate?: Date | null;
	public campaignStartedAt?: Date;
	public pinpointCampaignId?: string;

	public createdAt: Date;
	public updatedAt: Date;
	public deletedAt?: Date;

	public createdByUser?: UserDto;
	public community?: UserDto;
}

export class CampaignBlastMetricsDto {
	public Key: string;
	public Value: string;
	public Type: string;
}

export class CampaignBlastWithMetricsDto extends CampaignBlastDto {
	public openRate?: CampaignBlastMetricsDto;
	public hardBounceRate?: CampaignBlastMetricsDto;
}
